import { useEffect, useMemo, useRef, useState } from "react";
import { Icons } from "assets/svgs";
import { Dropdown } from "primereact/dropdown";

// Windowed page list: 1 … (cur-1) cur (cur+1) … last. `current` is 1-based.
const buildPages = (current, totalPages) => {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);

  const pages = [1];
  const left = Math.max(2, current - 1);
  const right = Math.min(totalPages - 1, current + 1);

  if (left > 2) pages.push("…");
  for (let p = left; p <= right; p++) pages.push(p);
  if (right < totalPages - 1) pages.push("…");
  pages.push(totalPages);

  return pages;
};

/**
 * Compact, Tailwind-only pagination bar (no styled-components). Keeps PrimeReact's
 * `onPageChange({ first, rows, page })` payload (page is 0-based) so it's a drop-in for Paginator.
 */
export const TablePagination = ({
  first = 0,
  rows = 25,
  totalRecords = 0,
  rowsPerPageOptions = [25, 50, 100],
  onPageChange,
  className = "",
}) => {
  const totalPages = Math.max(1, Math.ceil(totalRecords / rows));
  const current = Math.floor(first / rows) + 1; // 1-based
  const start = totalRecords === 0 ? 0 : first + 1;
  const end = Math.min(first + rows, totalRecords);

  const pages = useMemo(() => buildPages(current, totalPages), [current, totalPages]);

  // Inside a FormModal, the Rows panel has to leave this bar entirely. Kept
  // here (rather than appendTo="self") for two separate reasons:
  //
  //  - This bar sets `overflow-x-auto`, and CSS computes the *other* axis from
  //    `visible` to `auto` whenever one axis isn't visible -- so this ~40px
  //    strip is silently scrollable vertically too. PrimeReact scrolls the
  //    highlighted option into view on open, which scrolled this strip's own
  //    contents (counts, Rows control, page buttons) out of sight.
  //  - The panel was also clipped by FormModal's `overflow-y-auto` body, since
  //    it opens downward whenever the viewport has room below the trigger even
  //    though the modal body ends sooner.
  //
  // appendTo={document.body} is NOT the escape hatch here: FormModal's
  // useScrollLock pins `position: fixed; top: -<scrollY>px` on document.body,
  // which both makes body the panel's containing block and shifts it up by
  // scrollY, while PrimeReact positions body-appended panels at
  // getBoundingClientRect().top + getWindowScrollTop() (0 once body is fixed)
  // -- landing the panel scrollY px off. That is the "visibly detached" panel
  // ColumnConfigModal and AddRuleModal each hit and reverted. The overlay is
  // `fixed inset-0`, so its padding box origin IS the viewport origin, which
  // matches those same viewport coordinates exactly, and it never scrolls or
  // clips. Outside a modal there is no overlay and no scroll lock, so plain
  // "self" positioning stays as-is.
  const rootRef = useRef(null);
  const [appendTarget, setAppendTarget] = useState("self");

  useEffect(() => {
    setAppendTarget(rootRef.current?.closest("[data-modal-overlay]") ?? "self");
  }, []);

  const goTo = (page1) => {
    const clamped = Math.min(Math.max(1, page1), totalPages);
    if (clamped === current) return;
    onPageChange?.({ first: (clamped - 1) * rows, rows, page: clamped - 1 });
  };

  const changeRows = (newRows) => onPageChange?.({ first: 0, rows: newRows, page: 0 });

  const navBtn =
    "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#dddddd] bg-white text-[#5A5A5A] transition-colors hover:border-[#437f55] hover:text-[#437f55] disabled:pointer-events-none disabled:opacity-40";

  return (
    <div ref={rootRef} className={`flex w-full items-center justify-between gap-4 overflow-x-auto ${className}`}>
      <span className="whitespace-nowrap text-sm text-[#5A5A5A]">
        {totalRecords === 0 ? "0 of 0" : `${start}–${end}`}
        {totalRecords > 0 ? <span className="text-[#808080]">{` of ${totalRecords}`}</span> : null}
      </span>

      <div className="flex shrink-0 items-center gap-4">
        <span className="flex items-center gap-2 whitespace-nowrap text-sm text-[#5A5A5A]">
          Rows
          {/* A plain <select> here used to fall back to the browser's own
              unstyled native options popup (dark on some OS/browser
              combinations, positioned by the browser rather than this
              design system) instead of the app's own dropdown look --
              same styled Dropdown every other select-like control on
              Maverick Settings uses. */}
          <Dropdown
            appendTo={appendTarget}
            className="h-10 text-sm font-semibold text-[#333] [&_.p-dropdown-label]:flex [&_.p-dropdown-label]:!items-center [&_.p-dropdown-label]:!py-0 [&_.p-dropdown-label]:!pl-3 [&_.p-dropdown-label]:!pr-1 [&_.p-dropdown-label]:!text-sm [&_.p-dropdown-trigger]:!w-7"
            options={rowsPerPageOptions.map((opt) => ({ label: String(opt), value: opt }))}
            // rows-pp-panel caps this panel shorter than the app-wide 250px
            // dropdown cap (see index.css). This one only ever lists
            // rowsPerPageOptions, and it sits at the bottom of whatever list
            // it paginates, so a shorter panel is what keeps PrimeReact's
            // flip-up/down math able to find room for it on short windows.
            // The z-index has to clear anything positioned INSIDE the modal
            // card, not just the card itself: once portaled the panel is the
            // card's sibling, so it shares the overlay's stacking context
            // rather than sitting inside the card's. It must also be
            // !important -- PrimeReact's ZIndexUtils writes an inline
            // z-index on the panel, which beats any non-important class.
            panelClassName="rows-pp-panel !z-[11000] [&_.p-dropdown-items]:!py-1 [&_.p-dropdown-item]:!py-1.5 [&_.p-dropdown-item]:!px-3 [&_.p-dropdown-item]:!text-sm [&_.p-dropdown-item.p-highlight]:!bg-[#eef6f0] [&_.p-dropdown-item.p-highlight]:!text-[#245d38] [&_.p-dropdown-item:not(.p-highlight):not(.p-disabled):hover]:!bg-gray-100"
            value={rows}
            onChange={(e) => changeRows(e.value)}
          />
        </span>

        <div className="flex items-center gap-1.5">
          <button
            aria-label="Previous page"
            className={navBtn}
            disabled={current <= 1}
            type="button"
            onClick={() => goTo(current - 1)}
          >
            <Icons.ChevronRightIcon className="h-4 w-4 rotate-180" />
          </button>

          {pages.map((p, i) =>
            p === "…" ? (
              <span key={`gap-${i}`} className="flex h-10 w-7 items-center justify-center text-sm text-[#808080]">
                …
              </span>
            ) : (
              <button
                key={p}
                aria-current={p === current ? "page" : undefined}
                className={`flex h-10 min-w-10 items-center justify-center rounded-lg border px-3 text-sm font-semibold transition-colors ${
                  p === current
                    ? "border-[#437f55] bg-[#437f55] text-white"
                    : "border-[#dddddd] bg-white text-[#333] hover:border-[#437f55] hover:text-[#437f55]"
                }`}
                type="button"
                onClick={() => goTo(p)}
              >
                {p}
              </button>
            )
          )}

          <button
            aria-label="Next page"
            className={navBtn}
            disabled={current >= totalPages}
            type="button"
            onClick={() => goTo(current + 1)}
          >
            <Icons.ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
